import { motion } from "framer-motion";
import { formatEther } from "viem";
import {
    Trophy, Flame, Layers, Coins, Sparkles, Users, Loader2,
    Crown, Lock, ArrowUpRight,
} from "lucide-react";
import { useHallStats } from "@/hooks/useHallStats";
import { useLeaderboards } from "@/hooks/useLeaderboards";
import { useRecentMints, type GalleryItem } from "@/hooks/useGallery";
import { fmtCompact, fmtPct } from "@/lib/format";
import { CountUp } from "@/components/CountUp";
import { PiggyMark } from "@/components/Logo";
import { ContractsPanel } from "@/components/ContractsPanel";
import { EXPLORER_ADDR } from "@/lib/config";
import { useState } from "react";

const RARITY_LABEL = ["Unrevealed", "Piglet", "Yearling", "Hog", "Razorback", "Royal Sow", "Cosmic Piggy"];
const RARITY_RING: Record<number, string> = {
    0: "ring-white/[0.06]",
    1: "ring-pink-300/30",
    2: "ring-pink-400/40",
    3: "ring-pink-500/40",
    4: "ring-pink-600/50",
    5: "ring-piggy-gold-400/60",
    6: "ring-piggy-pink-300/70 shadow-glow",
};
const RARITY_SAMPLE: Record<string, string> = {
    "Piglet":        "/nft-img/sample-piglet.png",
    "Yearling":      "/nft-img/sample-yearling.png",
    "Hog":           "/nft-img/sample-hog.png",
    "Razorback":     "/nft-img/sample-razorback.png",
    "Royal Sow":     "/nft-img/sample-royal_sow.png",
    "Cosmic Piggy":  "/nft-img/sample-cosmic_piggy.png",
};

// Sanity-check upstream JSON before piping into an <a href> — the leaderboards
// function returns event-derived addresses and a malformed value would
// otherwise become a malicious href on click. (Audit fix follow-on to H9/M3.)
const isValidAddr = (s: string): boolean => /^0x[0-9a-fA-F]{40}$/.test(s);

const fadeUp = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-50px" },
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
};


export default function Hall() {
    const { stats, live } = useHallStats();
    const { data, loading, error } = useLeaderboards();

    const burnedPct = stats.totalSupply > 0n
        ? (Number(stats.burned) / Number(stats.totalSupply)) * 100
        : 0;

    return (
        <section className="max-w-6xl mx-auto px-6 py-16">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="text-center mb-12"
            >
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass-pink mb-4 text-xs font-semibold text-piggy-pink-200">
                    <Trophy className="w-3 h-3" />
                    The on-chain trophy case
                </div>
                <h1 className="display text-5xl md:text-6xl font-black tracking-tighter mb-4">
                    Hall of <span className="gradient-text-shimmer">Piggys</span>
                </h1>
                <p className="text-piggy-cream/60 text-lg max-w-2xl mx-auto">
                    The legends of the Hood, carved on-chain. Every meaningful
                    action on the Safe Piggy protocol — referrals, stakes,
                    mints — aggregated live from Robinhood Chain events.
                    No off-chain magic; everything you see is verifiable on
                    Blockscout.
                </p>
            </motion.div>

            <motion.section {...fadeUp} className="mb-16">
                <h2 className="display text-2xl font-bold mb-5 text-center">Protocol all-time</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <BigStat
                        icon={<Flame />}
                        label="PIGGY burned"
                        value={<CountUp value={Number(stats.burned) / 1e18} format={(n) => fmtCompact(n)} />}
                        subValue={`${fmtPct(burnedPct * 100, 3)} of supply`}
                        accent
                    />
                    <BigStat
                        icon={<Layers />}
                        label="LP rewards paid"
                        value={<CountUp value={Number(stats.totalLpReceived) / 1e18} format={(n) => n.toFixed(4)} />}
                        subValue="ETH to LP stakers"
                    />
                    <BigStat
                        icon={<Coins />}
                        label="PIGGY stake rewards"
                        value={<CountUp value={Number(stats.totalPiggyStakingReceived) / 1e18} format={(n) => n.toFixed(4)} />}
                        subValue="ETH to PIGGY stakers"
                    />
                    <BigStat
                        icon={<Sparkles />}
                        label="Piggies minted"
                        value={<CountUp value={Number(stats.nftMinted)} format={(n) => n.toFixed(0)} />}
                        subValue={`of ${stats.nftMaxSupply.toString()} max`}
                    />
                </div>
                <div className="text-center mt-4 text-xs text-piggy-cream/40">
                    {live ? "Live from Robinhood Chain" : "Demo data — contracts not yet deployed"}
                </div>
            </motion.section>

            <motion.section {...fadeUp} className="mb-16">
                <div className="flex items-baseline justify-between mb-5">
                    <h2 className="display text-3xl md:text-4xl font-black">
                        Live <span className="gradient-text">leaderboards</span>
                    </h2>
                    {loading && <Loader2 className="w-4 h-4 animate-spin text-piggy-cream/40" />}
                    {error && <span className="text-xs text-red-400">leaderboards offline</span>}
                </div>

                <div className="grid lg:grid-cols-2 gap-5">
                    <Leaderboard
                        title="Top referrers"
                        icon={<Users className="w-4 h-4" />}
                        empty={data.referrers.length === 0}
                        rows={data.referrers.map((r) => ({
                            primary: r.addressShort,
                            address: r.address,
                            stat: `${fmtCompact(Number(r.volume) / 1e18)} PIGGY`,
                            sub: `${r.count.toString()} buys · ${formatEther(r.earned).slice(0, 8)} ETH`,
                        }))}
                    />

                    <Leaderboard
                        title="Top LP stakers"
                        icon={<Lock className="w-4 h-4" />}
                        empty={data.lpStakers.length === 0}
                        rows={data.lpStakers.map((r) => ({
                            primary: r.addressShort,
                            address: r.address,
                            stat: `${fmtCompact(Number(r.staked) / 1e18, 4)} LP`,
                            sub: `${(r.multiplierBps / 10000).toFixed(2)}× · ${formatEther(r.earned).slice(0, 8)} ETH earned`,
                        }))}
                    />

                    <Leaderboard
                        title="Top PIGGY stakers"
                        icon={<Coins className="w-4 h-4" />}
                        empty={data.piggyStakers.length === 0}
                        rows={data.piggyStakers.map((r) => ({
                            primary: r.addressShort,
                            address: r.address,
                            stat: `${fmtCompact(Number(r.staked) / 1e18)} PIGGY`,
                            sub: `${formatEther(r.earned).slice(0, 8)} ETH earned`,
                        }))}
                    />

                    <Leaderboard
                        title="Top minters"
                        icon={<Crown className="w-4 h-4" />}
                        empty={data.topMinters.length === 0}
                        rows={data.topMinters.map((r) => ({
                            primary: r.addressShort,
                            address: r.address,
                            stat: `${r.nfts.toString()} NFTs`,
                            sub: data.nfts.minted > 0n
                                ? `${((Number(r.nfts) / Number(data.nfts.minted)) * 100).toFixed(1)}% of minted`
                                : "",
                        }))}
                    />
                </div>
            </motion.section>

            <ContractsPanel />

            <RecentMintsGallery />

            <motion.section {...fadeUp}>
                <h2 className="display text-2xl font-bold mb-5 text-center">Genesis rarity allocation</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    {[
                        { tier: "Piglet",       count: 3000, pct: 60.0, grad: "from-pink-200 to-pink-400" },
                        { tier: "Yearling",     count: 1250, pct: 25.0, grad: "from-pink-300 to-pink-500" },
                        { tier: "Hog",          count:  500, pct: 10.0, grad: "from-pink-400 to-pink-600" },
                        { tier: "Razorback",    count:  200, pct:  4.0, grad: "from-pink-500 to-pink-700" },
                        { tier: "Royal Sow",    count:   45, pct:  0.9, grad: "from-piggy-gold-400 to-piggy-pink-700" },
                        { tier: "Cosmic Piggy", count:    5, pct:  0.1, grad: "from-piggy-pink-300 via-piggy-gold-400 to-piggy-violet" },
                    ].map((r) => (
                        <div key={r.tier} className="glass rounded-2xl p-4">
                            <div className={`aspect-square rounded-xl overflow-hidden bg-gradient-to-br ${r.grad} mb-3`}>
                                <img
                                    src={RARITY_SAMPLE[r.tier] ?? "/piggy.png"}
                                    alt={`${r.tier} sample`}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                    draggable={false}
                                />
                            </div>
                            <div className="display text-sm font-bold text-piggy-cream">{r.tier}</div>
                            <div className="text-xs text-piggy-cream/40 tabular-nums">
                                {r.count} ({r.pct}%)
                            </div>
                        </div>
                    ))}
                </div>
            </motion.section>
        </section>
    );
}

// ============================================================================
// Recent mints gallery
// ============================================================================

function RecentMintsGallery() {
    const { items, totalMinted, loading } = useRecentMints(24);
    return (
        <motion.section {...fadeUp} className="mb-16">
            <div className="flex items-baseline justify-between mb-5 flex-wrap gap-2">
                <h2 className="display text-3xl md:text-4xl font-black">
                    Recently <span className="gradient-text">minted</span>
                </h2>
                <div className="text-sm text-piggy-cream/50 tabular-nums">
                    {totalMinted.toLocaleString()} / 5,000 minted
                </div>
            </div>

            {loading && items.length === 0 ? (
                <div className="glass rounded-3xl px-6 py-16 text-center text-piggy-cream/40">
                    <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                </div>
            ) : items.length === 0 ? (
                <div className="glass rounded-3xl px-6 py-16 text-center">
                    <div className="text-sm text-piggy-cream/50 mb-2">No piggies minted yet.</div>
                    <a href="/mint" className="text-piggy-pink-300 text-sm hover:text-piggy-pink-200">
                        Be the first → /mint
                    </a>
                </div>
            ) : (
                <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                    {items.map((item) => <PiggyTile key={item.tokenId.toString()} {...item} />)}
                </div>
            )}
        </motion.section>
    );
}

function PiggyTile({ tokenId, rarity }: GalleryItem) {
    const [errored, setErrored] = useState(false);
    const revealed = rarity > 0;
    const label = RARITY_LABEL[rarity] ?? "Unrevealed";
    const ringClass = RARITY_RING[rarity] ?? "ring-white/[0.06]";

    return (
        <div className={`glass rounded-2xl p-2.5 ring-1 ${ringClass} transition-transform hover:-translate-y-0.5`}>
            <div className="aspect-square rounded-xl overflow-hidden mb-2 bg-black/30 grid place-items-center">
                {revealed && !errored ? (
                    <img
                        src={`/nft-img/${tokenId.toString()}.png`}
                        alt={`Safe Piggy #${tokenId.toString()}`}
                        onError={() => setErrored(true)}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        draggable={false}
                    />
                ) : (
                    <PiggyMark size={48} hueRotate={Number(tokenId) % 360} />
                )}
            </div>
            <div className="flex items-baseline justify-between px-1">
                <span className="font-mono text-xs text-piggy-cream/50">#{tokenId.toString()}</span>
                <span className="text-[10px] font-semibold uppercase tracking-wider text-piggy-cream/60">{label}</span>
            </div>
        </div>
    );
}

function BigStat({
    icon, label, value, subValue, accent,
}: {
    icon: React.ReactNode;
    label: string;
    value: React.ReactNode;
    subValue?: string;
    accent?: boolean;
}) {
    return (
        <div className={`glass rounded-2xl p-5 ${accent ? "ring-1 ring-piggy-pink-500/30 shadow-glow" : ""}`}>
            <div className={`w-9 h-9 rounded-xl grid place-items-center mb-3 ${
                accent ? "bg-piggy-pink-500/20 text-piggy-pink-300" : "bg-white/[0.04] text-piggy-cream/60"
            }`}>
                {icon}
            </div>
            <div className="text-xs text-piggy-cream/40 uppercase tracking-wider mb-1">{label}</div>
            <div className={`display text-2xl font-bold ${accent ? "gradient-text" : "text-piggy-cream"}`}>
                {value}
            </div>
            {subValue && <div className="text-xs text-piggy-cream/40 mt-0.5">{subValue}</div>}
        </div>
    );
}

interface LeaderboardRow {
    primary: string;
    address: string;
    stat: string;
    sub: string;
}

function Leaderboard({
    title, icon, empty, rows,
}: {
    title: string; icon: React.ReactNode; empty: boolean; rows: LeaderboardRow[];
}) {
    return (
        <div className="glass rounded-3xl overflow-hidden">
            <div className="flex items-center gap-2 px-6 py-4 border-b border-white/[0.06]">
                <div className="w-7 h-7 rounded-lg bg-piggy-pink-500/20 text-piggy-pink-300 grid place-items-center">
                    {icon}
                </div>
                <h3 className="display font-bold text-piggy-cream">{title}</h3>
            </div>

            {empty ? (
                <div className="px-6 py-10 text-center">
                    <div className="text-sm text-piggy-cream/40 mb-1">No legends yet</div>
                    <div className="text-xs text-piggy-cream/30">
                        Be the first — check the dapp navigation above
                    </div>
                </div>
            ) : (
                rows.slice(0, 10).map((row, i) => (
                    <a
                        key={row.address}
                        href={isValidAddr(row.address) ? `${EXPLORER_ADDR}/${row.address}` : undefined}
                        target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-4 px-6 py-3 border-b border-white/[0.04] last:border-b-0 hover:bg-white/[0.02] group"
                    >
                        <div className={`display text-xl font-black w-8 tabular-nums ${i === 0 ? "gradient-text" : "text-piggy-cream/30"}`}>
                            {(i + 1).toString().padStart(2, "0")}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="font-mono text-sm text-piggy-cream truncate">{row.primary}</div>
                            <div className="text-xs text-piggy-cream/40 truncate">{row.sub}</div>
                        </div>
                        <div className="text-right flex-shrink-0">
                            <div className="display font-bold text-piggy-cream tabular-nums">{row.stat}</div>
                        </div>
                        <ArrowUpRight className="w-3 h-3 text-piggy-cream/30 group-hover:text-piggy-cream/70 flex-shrink-0" />
                    </a>
                ))
            )}
        </div>
    );
}
